/**
 * AI model prices (TMMPDM-0011). TMMPDM-0035: the model whose history is open is the URL's
 * `history`. TMMPDM-0088: platform AI controls. TMMPDM-0099: Prices and Platform AI controls are
 * tabs; billing attention is its own page. The server's "As of" sits on the Prices tab.
 * TMMPDM-0103: four tabs — Models (the prices, with Add model…), one per tier, and Platform AI
 * controls. The old `?tab=prices` lands on Models with the URL replaced. TMMPDM-0104: the Models
 * tab is the AI model catalogue — one read each of the catalogue and the prices — and Add model…
 * adds a catalogue model, verified with Anthropic. "Show retired" is the URL's `retired=1`.
 * TMMPDM-0112: a fifth tab, Naming quality (tmm-api 0194 U3), the read-only "Is naming getting better?"
 * report.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, text, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { ErrorState, PageHeader, useNotify } from '@/components/page';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { adminApi } from '@/api/tmm/admin';
import type { AiModelCatalogList, AiModelPriceListResponse } from '@/api/tmm/types';
import { formatDate } from '@/format';
import { ModelAssignments } from './ai/ModelAssignments';
import { ModelCatalog } from './ai/ModelCatalog';
import { NamingQuality } from './ai/NamingQuality';
import { AddModelDialog } from './ai/ModelCatalogDialogs';
import { modelName } from './ai/modelCatalogCopy';
import { PlatformAiControls } from './ai/PlatformAiControls';

const PRICE_TABS = ['models', 'fast', 'deep', 'controls', 'naming'] as const;

/** The Prices tab before TMMPDM-0103. */
const RETIRED_PRICES_TAB = 'prices';

type PricesView = {
  history: string | undefined;
  tab: (typeof PRICE_TABS)[number];
  retired: '1' | undefined;
};

const VIEW: UrlSchema<PricesView> = {
  history: { default: undefined, parse: text },
  tab: { default: 'models', parse: oneOf(PRICE_TABS) },
  retired: { default: undefined, parse: oneOf(['1'] as const) },
};

interface ModelsData {
  catalogue: AiModelCatalogList;
  prices: AiModelPriceListResponse;
}

export function AiModelPrices() {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  const [searchParams] = useSearchParams();
  const retiredTab = searchParams.get('tab') === RETIRED_PRICES_TAB;
  useEffect(() => {
    // Replace, so Back never returns to the retired link.
    if (retiredTab) setView({ tab: 'models' }, { history: 'replace' });
  }, [retiredTab, setView]);
  const [addingModel, setAddingModel] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data: ModelsData | null;
    error: boolean;
    failure?: unknown;
  }>({ key: '', data: null, error: false });
  const key = String(reloadToken);
  const onModels = view.tab === 'models';

  useEffect(() => {
    // Each visit to the Models tab reads again: an assignment made on a tier tab changes `usedBy`.
    if (!onModels) return;
    let cancelled = false;
    Promise.all([adminApi.listAiModels(), adminApi.listAiModelPrices()])
      .then(([catalogue, prices]) => {
        if (!cancelled) setState({ key, data: { catalogue, prices }, error: false });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ key, data: null, error: true, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [key, onModels]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);
  const current = state.key === key;
  const error = current && state.error;
  // The last good read stays on screen while a re-read after a write is in flight.
  const data = state.data;

  return (
    <Box>
      <PageHeader
        title={t('page.aiModelPrices.title', 'AI model prices')}
        subtitle={t(
          'page.aiModelPrices.subtitle',
          "What the models cost us, and the platform's own AI limits.",
        )}
        meta={
          view.tab === 'models' && data && !error ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('ai.asOf', 'As of {{date}}', { date: formatDate(data.prices.asOf, locale) })}
            </Typography>
          ) : null
        }
        {...(view.tab === 'models'
          ? {
              primaryAction: {
                label: t('ai.add', 'Add model…'),
                onClick: () => setAddingModel(true),
              },
            }
          : {})}
      />

      <FilterTabs
        ariaLabel={t('ai.tabs.aria', 'AI model prices sections')}
        active={view.tab}
        onChange={(next) => {
          const tab = oneOf(PRICE_TABS)(next);
          if (tab) setView({ tab });
        }}
        tabs={[
          { key: 'models', label: t('ai.tabs.models', 'Models') },
          { key: 'fast', label: t('ai.tabs.fast', 'Fast tier assignment') },
          { key: 'deep', label: t('ai.tabs.deep', 'Deep tier assignment') },
          { key: 'controls', label: t('platformAiControls.title', 'Platform AI controls') },
          { key: 'naming', label: t('ai.tabs.naming', 'Naming quality') },
        ]}
      />

      {view.tab === 'fast' || view.tab === 'deep' ? (
        <Box sx={{ mt: 2 }}>
          <ModelAssignments key={view.tab} tier={view.tab === 'fast' ? 'FAST' : 'DEEP'} />
        </Box>
      ) : view.tab === 'models' ? (
        <Box sx={{ mt: 2 }}>
          {error ? (
            <ErrorState
              error={state.failure}
              message={t('ai.loadError', 'Could not load the AI models and prices.')}
              onRetry={refetch}
            />
          ) : data === null ? (
            <Typography variant="body2" color="text.secondary">
              {t('common.loading', 'Loading')}
            </Typography>
          ) : (
            <ModelCatalog
              models={data.catalogue.models}
              prices={data.prices.prices}
              locale={locale}
              showRetired={view.retired === '1'}
              onShowRetiredChange={(show) => setView({ retired: show ? '1' : undefined })}
              historyModel={view.history}
              onToggleHistory={(model) =>
                setView({ history: view.history === model ? undefined : model })
              }
              onReload={refetch}
            />
          )}
        </Box>
      ) : view.tab === 'naming' ? (
        <Box sx={{ mt: 2 }}>
          <NamingQuality />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <PlatformAiControls />
        </Box>
      )}

      <AddModelDialog
        open={addingModel}
        onClose={() => setAddingModel(false)}
        onAdded={(row) => {
          setAddingModel(false);
          notify.success(t('ai.catalog.added', '{{model}} added.', { model: modelName(row) }));
          refetch();
        }}
      />
    </Box>
  );
}
